'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle2, Film, Send, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export type ToastType = 'approved' | 'rendering' | 'published';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  timestamp: number;
}

interface ToastContextValue {
  addToast: (type: ToastType, title: string, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const TOAST_DURATION = 5000;

const toastConfig: Record<ToastType, { icon: React.ElementType; iconClass: string; borderClass: string; labelClass: string; label: string }> = {
  approved: {
    icon: CheckCircle2,
    iconClass: 'text-emerald-400',
    borderClass: 'border-emerald-500/30 bg-emerald-500/5',
    labelClass: 'text-emerald-400',
    label: 'Approved',
  },
  rendering: {
    icon: Film,
    iconClass: 'text-sky-400',
    borderClass: 'border-sky-500/30 bg-sky-500/5',
    labelClass: 'text-sky-400',
    label: 'Rendering',
  },
  published: {
    icon: Send,
    iconClass: 'text-violet-400',
    borderClass: 'border-violet-500/30 bg-violet-500/5',
    labelClass: 'text-violet-400',
    label: 'Published',
  },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const cfg = toastConfig[toast.type];
  const Icon = cfg.icon;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    const t = setTimeout(handleDismiss, TOAST_DURATION);
    return () => clearTimeout(t);
  }, [handleDismiss]);

  return (
    <div
      className={`
        flex items-start gap-3 w-80 rounded-lg border px-4 py-3 shadow-lg
        card-elevated transition-all duration-300
        ${cfg.borderClass}
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}
      `}
    >
      <div className={`mt-0.5 shrink-0 ${cfg.iconClass}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-xs font-semibold font-mono uppercase tracking-wider ${cfg.labelClass}`}>
            {cfg.label}
          </span>
        </div>
        <p className="text-sm font-medium text-foreground leading-snug truncate">{toast.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{toast.message}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((type: ToastType, title: string, message: string) => {
    counterRef.current += 1;
    const id = `toast-${Date.now()}-${counterRef.current}`;
    setToasts((prev) => [...prev.slice(-4), { id, type, title, message, timestamp: Date.now() }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Toast container — bottom-right */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
